import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import AutoScrollCarousel from "@/components/AutoScrollCarousel";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useRef, useState } from "react";
import SEO from "@/components/SEO";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Skeleton for publisher cards
const PublisherSkeleton = () => (
  <div className="w-[calc(50%-0.75rem)] sm:w-[calc(33.333%-1.25rem)] md:w-[calc(25%-1.5rem)] lg:w-[calc(20%-1.6rem)] xl:w-[calc(16.666%-1.4rem)] max-w-[180px]">
    <Skeleton className="aspect-[3/4] rounded-lg" />
    <Skeleton className="h-4 w-3/4 mx-auto mt-3" />
    <Skeleton className="h-3 w-1/2 mx-auto mt-1.5" />
  </div>
);

const Index = () => {
  const [scrollY, setScrollY] = useState(0);
  const latestSectionRef = useRef<HTMLElement>(null);

  // Parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { data: publishers, isLoading: publishersLoading } = useQuery({
    queryKey: ["publishers-with-covers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("publishers")
        .select("*")
        .or("archived.is.null,archived.eq.false")
        .order("name");
      
      if (error) throw error;
      return data;
    },
  });

  const getPublisherCoverUrl = (slug: string) => {
    return `${SUPABASE_URL}/storage/v1/object/public/rights-guides-covers/${slug}_rights-guides-covers.jpg`;
  };

  const { data: latestBooks, isLoading: booksLoading } = useQuery({
    queryKey: ["latest-books"],
    queryFn: async () => {
      // Fetch more books to allow filtering for diversity
      const { data, error } = await supabase
        .from("books")
        .select("id, title, cover_url, slug, author_name, publisher_id, created_at, updated_at")
        .or("archived.is.null,archived.eq.false")
        .order("updated_at", { ascending: false, nullsFirst: false })
        .limit(100);
      
      if (error) throw error;
      if (!data) return [];

      // Sort by freshness: prioritize updated_at, then created_at
      const sortedByFreshness = [...data].sort((a, b) => {
        const aDate = new Date(a.updated_at || a.created_at || 0).getTime();
        const bDate = new Date(b.updated_at || b.created_at || 0).getTime();
        return bDate - aDate;
      });

      // Ensure diversity: max 3 books per publisher
      const publisherCount: Record<string, number> = {};
      const diverseBooks = sortedByFreshness.filter(book => {
        const pubId = String(book.publisher_id || 'unknown');
        publisherCount[pubId] = (publisherCount[pubId] || 0) + 1;
        return publisherCount[pubId] <= 3;
      });

      // Return top 20 diverse books
      return diverseBooks.slice(0, 20);
    },
  });

  // Calculate parallax offset for latest entries section
  const getParallaxOffset = () => {
    if (!latestSectionRef.current) return 0;
    const rect = latestSectionRef.current.getBoundingClientRect();
    const offset = (rect.top / window.innerHeight) * 30;
    return Math.max(-15, Math.min(15, offset));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Children's Books Rights Agency"
        description="Aude Rights Agency represents publishers of children's books and adult non-fiction from Germany, France, Greece, Poland and Italy. Discover our catalogue of rights guides."
        type="website"
      />
      <Navigation />
      
      {/* Rights Guides Section - Hero */}
      <section className="py-12 md:py-16 lg:py-20 bg-background">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Animated title */}
          <h1 
            className="font-noto text-3xl md:text-4xl lg:text-5xl font-bold text-primary text-center mb-10 md:mb-12 animate-fade-in-up"
            style={{ animationFillMode: 'backwards' }}
          >
            RIGHTS GUIDES
          </h1>
          
          <div className="flex flex-wrap justify-center gap-6 md:gap-8">
            {publishersLoading ? (
              // Skeleton loading state
              Array.from({ length: 6 }).map((_, i) => (
                <PublisherSkeleton key={i} />
              ))
            ) : (
              publishers?.map((publisher, index) => (
                <Link
                  key={publisher.id}
                  to={`/publishers/${publisher.slug}`}
                  className="group w-[calc(50%-0.75rem)] sm:w-[calc(33.333%-1.25rem)] md:w-[calc(25%-1.5rem)] lg:w-[calc(20%-1.6rem)] xl:w-[calc(16.666%-1.4rem)] max-w-[180px] animate-scale-in"
                  style={{ 
                    animationDelay: `${index * 80}ms`,
                    animationFillMode: 'backwards'
                  }}
                >
                  <div className="aspect-[3/4] overflow-hidden rounded-lg bg-muted shadow-soft transition-all duration-300 group-hover:shadow-elevated group-hover:-translate-y-2">
                    <img
                      src={getPublisherCoverUrl(publisher.slug)}
                      alt={publisher.name}
                      width={180}
                      height={240}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading={index < 6 ? "eager" : "lazy"}
                      fetchPriority={index < 2 ? "high" : undefined}
                      decoding={index < 6 ? "sync" : "async"}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden flex h-full items-center justify-center text-muted-foreground text-xs p-2">
                      {publisher.name}
                    </div>
                  </div>
                  <div className="mt-3 text-center">
                    <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors duration-200">
                      {publisher.name}
                    </p>
                    {publisher.country && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {publisher.country}
                      </p>
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Latest Entries Section with parallax */}
      <section 
        ref={latestSectionRef}
        className="py-12 md:py-16 lg:py-20 bg-card parallax-container"
      >
        <div 
          className="container mx-auto px-6 lg:px-12"
          style={{ transform: `translateY(${getParallaxOffset()}px)` }}
        >
          <div className="flex justify-between items-end mb-8 animate-slide-in-bottom" style={{ animationFillMode: 'backwards' }}>
            <div>
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-2">
                Latest Entries
              </h2>
              <p className="text-muted-foreground">Recently added to our catalogue</p>
            </div>
            <Link to="/books" className="hidden sm:block">
              <Button variant="ghost" className="text-primary hover:text-primary/80 btn-press">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          {booksLoading ? (
            // Skeleton for carousel
            <div className="flex gap-4 overflow-hidden">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-32 sm:w-40">
                  <Skeleton className="aspect-[3/4] rounded-lg" />
                  <Skeleton className="h-4 w-full mt-2" />
                  <Skeleton className="h-3 w-2/3 mt-1" />
                </div>
              ))}
            </div>
          ) : latestBooks && latestBooks.length > 0 ? (
            <AutoScrollCarousel 
              books={latestBooks} 
              title=""
              showAuthor
            />
          ) : null}
          
          <Link to="/books" className="sm:hidden mt-6 block">
            <Button variant="outline" className="w-full btn-press">
              View All Books <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
